import React from "react";
import { useGoogleLogin, type TokenResponse } from "@react-oauth/google";
import { fireAnalytics } from "../../../utils/firebase";
import { getSessionId, getDeviceCategory } from "../../../utils/analyticsHelpers.ts";
import styles from "./GoogleSignInButton.module.scss";

export type GoogleSignInAnalyticsSource = "work_bag_header" | "jobboard_registration";

interface GoogleSignInButtonProps {
  onAccessToken: (accessToken: string) => void;
  disabled?: boolean;
  analyticsSource?: GoogleSignInAnalyticsSource;
  compact?: boolean;
  layout?: "default" | "embedded";
}

const GoogleSignInButton: React.FC<GoogleSignInButtonProps> = ({
  onAccessToken,
  disabled = false,
  analyticsSource = "work_bag_header",
  compact = false,
  layout = "default",
}) => {
  const login = useGoogleLogin({
    onSuccess: (tokenResponse: TokenResponse) => {
      fireAnalytics("jobboard_google_oauth_started", {
        category: "authentication",
        session_id: getSessionId(),
        device_category: getDeviceCategory(),
        action: "google_job_board_oauth",
        source: analyticsSource,
      });
      if (tokenResponse.access_token) {
        onAccessToken(tokenResponse.access_token);
      }
    },
    onError: () => {
      fireAnalytics("jobboard_google_oauth_error", {
        category: "authentication",
        source: analyticsSource,
      });
    },
    scope: "openid email profile",
  });

  const isEmbedded = layout === "embedded";

  return (
    <div
      className={`${styles.wrap} ${compact ? styles.wrap_compact : ""} ${
        isEmbedded ? styles.wrap_embedded : ""
      }`}
    >
      <div className={`${styles.googleDivider} ${isEmbedded ? styles.googleDivider_embedded : ""}`}>
        <span className={`${styles.googleDivider_line} ${isEmbedded ? styles.googleDivider_line_embedded : ""}`} />
        <div className={`${styles.googleDivider_text} ${isEmbedded ? styles.googleDivider_text_embedded : ""}`}>
          {isEmbedded ? "o" : "O continúa con"}
        </div>
        <span className={`${styles.googleDivider_line} ${isEmbedded ? styles.googleDivider_line_embedded : ""}`} />
      </div>
      <button
        type="button"
        className={`${styles.googleButton} ${isEmbedded ? styles.googleButton_embedded : ""}`}
        onClick={() => login()}
        disabled={disabled}
      >
        <div className={styles.googleButton_content}>
          <svg className={styles.googleButton_icon} viewBox="0 0 24 24" width="24" height="24">
            <path
              fill="#234ba2"
              d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
            />
            <path
              fill="#2d5bc0"
              d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
            />
            <path
              fill="#eef3ff"
              d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
            />
            <path
              fill="#1e3d8a"
              d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
            />
          </svg>
          <span className={styles.googleButton_text}>Continuar con Google</span>
        </div>
      </button>
    </div>
  );
};

export default GoogleSignInButton;
